import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ClipboardList, Calendar, Filter, User } from 'lucide-react'

import { apiClient } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'

interface AuditLogEntry {
  id: string
  organization_id: string
  user_id: string
  user_display_name: string | null
  action: string
  resource_type: string | null
  resource_id: string | null
  details: Record<string, unknown>
  ip_address: string | null
  user_agent: string | null
  timestamp: string
}

interface PaginationInfo {
  page: number
  page_size: number
  total_items: number
  total_pages: number
}

interface AuditLogResponse {
  data: AuditLogEntry[]
  pagination: PaginationInfo
}

const ACTION_KEYS = [
  'user_login',
  'user_logout',
  'invoice_view',
  'invoice_list',
  'invoice_create',
  'invoice_update',
  'invoice_delete',
  'invoice_status_update',
  'trip_view',
  'dashboard_open',
  'user_create',
  'user_update',
  'user_delete',
  'user_invite',
  'organization_create',
  'organization_update',
  'cost_center_create',
  'cost_center_update',
  'cost_center_delete',
  'cost_center_permission_grant',
  'cost_center_permission_revoke',
  'impersonation_start',
  'impersonation_end',
]

const ACTION_COLORS: Record<string, string> = {
  user_login: 'bg-green-100 text-green-800',
  user_logout: 'bg-gray-100 text-gray-800',
  invoice_view: 'bg-blue-100 text-blue-800',
  invoice_status_update: 'bg-yellow-100 text-yellow-800',
  trip_view: 'bg-indigo-100 text-indigo-800',
  dashboard_open: 'bg-purple-100 text-purple-800',
  user_create: 'bg-teal-100 text-teal-800',
  user_update: 'bg-orange-100 text-orange-800',
  user_delete: 'bg-red-100 text-red-800',
  organization_update: 'bg-cyan-100 text-cyan-800',
  impersonation_start: 'bg-pink-100 text-pink-800',
  impersonation_end: 'bg-pink-50 text-pink-700',
}

export default function AuditLogPage() {
  const { t } = useTranslation('admin')
  const { t: tc } = useTranslation('common')
  const [page, setPage] = useState(1)
  const [actionFilter, setActionFilter] = useState<string>('')
  const [userNameFilter, setUserNameFilter] = useState<string>('')
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')

  const { data, isLoading, error } = useQuery<AuditLogResponse>({
    queryKey: ['auditLogs', page, actionFilter, userNameFilter, dateFrom, dateTo],
    queryFn: () => {
      let url = `/audit?page=${page}&page_size=50`
      if (actionFilter) url += `&action=${actionFilter}`
      if (userNameFilter) url += `&user_name=${encodeURIComponent(userNameFilter)}`
      if (dateFrom) url += `&date_from=${dateFrom}`
      if (dateTo) url += `&date_to=${dateTo}`
      return apiClient.get(url)
    },
  })

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    return new Intl.DateTimeFormat('fi-FI', {
      dateStyle: 'short',
      timeStyle: 'medium',
    }).format(date)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{tc('errors.generic')}</p>
      </div>
    )
  }

  const logs = data?.data || []
  const pagination = data?.pagination

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList className="w-7 h-7 text-primary-600" />
            {t('audit.title')}
          </h1>
          <p className="text-gray-600 mt-1">{t('audit.subtitle')}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Filter className="w-4 h-4 inline mr-1" />
              {t('audit.actionFilter')}
            </label>
            <select
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">{tc('common.all')}</option>
              {ACTION_KEYS.map((key) => (
                <option key={key} value={key}>{t(`audit.actions.${key}`, key)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <User className="w-4 h-4 inline mr-1" />
              {t('audit.userFilter')}
            </label>
            <input
              type="text"
              value={userNameFilter}
              onChange={(e) => { setUserNameFilter(e.target.value); setPage(1); }}
              placeholder={t('audit.userFilterPlaceholder')}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="w-4 h-4 inline mr-1" />
              {t('audit.dateFrom')}
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="w-4 h-4 inline mr-1" />
              {t('audit.dateTo')}
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          {(actionFilter || userNameFilter || dateFrom || dateTo) && (
            <button
              onClick={() => { setActionFilter(''); setUserNameFilter(''); setDateFrom(''); setDateTo(''); setPage(1); }}
              className="px-3 py-2 text-gray-600 hover:text-gray-800"
            >
              {t('audit.clearFilters')}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('audit.timestamp')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('audit.user')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('audit.action')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('audit.resource')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('audit.details')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('audit.ipAddress')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    {t('audit.noLogs')}
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatTimestamp(log.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {log.user_display_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-800'
                      }`}>
                        {t(`audit.actions.${log.action}`, log.action)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {log.resource_type && (
                        <span>
                          {log.resource_type}
                          {log.resource_id && (
                            <span className="text-gray-400 ml-1">
                              #{log.resource_id.slice(0, 8)}
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                      {log.details && Object.keys(log.details).length > 0 && (
                        <span className="font-mono text-xs">
                          {JSON.stringify(log.details).slice(0, 50)}
                          {JSON.stringify(log.details).length > 50 && '...'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                      {log.ip_address || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.total_pages > 1 && (
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {tc('common.page')} {pagination.page} / {pagination.total_pages} ({pagination.total_items} {tc('common.items')})
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                {tc('common.previous')}
              </button>
              <button
                onClick={() => setPage(p => Math.min(pagination.total_pages, p + 1))}
                disabled={page === pagination.total_pages}
                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                {tc('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
